(() => {
  const initializedBundles = new WeakSet();
  const ALLOWED_KITS = new Set(['1', '2', '3']);
  const ALLOWED_SIZES = new Set(['S', 'M', 'L']);

  const getVariantKey = (kit, size) => `${kit}|${size}`;

  const parseVariantData = (bundle) => {
    const script = bundle.querySelector('[data-magnefit-variants]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('[MagneFit bundle] No fue posible leer las variantes.', error);
      return {};
    }
  };

  const getState = (bundle) => ({
    kit: bundle.dataset.selectedKit || bundle.dataset.defaultKit || '2',
    size: bundle.dataset.selectedSize || bundle.dataset.defaultSize || 'S'
  });

  const setState = (bundle, nextState) => {
    const kit = ALLOWED_KITS.has(String(nextState.kit)) ? String(nextState.kit) : bundle.dataset.defaultKit || '2';
    const size = ALLOWED_SIZES.has(String(nextState.size)) ? String(nextState.size) : bundle.dataset.defaultSize || 'S';

    bundle.dataset.selectedKit = kit;
    bundle.dataset.selectedSize = size;
  };

  const getVariant = (variants, kit, size) => {
    const variant = variants[getVariantKey(kit, size)];

    if (!variant || !ALLOWED_KITS.has(String(variant.kit)) || !ALLOWED_SIZES.has(String(variant.size))) {
      return null;
    }

    return variant;
  };

  const updateCardPrice = (bundle, variants, kit, size) => {
    const variant = getVariant(variants, kit, size);
    const price = bundle.querySelector(`[data-magnefit-price="${kit}"]`);
    const compare = bundle.querySelector(`[data-magnefit-compare="${kit}"]`);

    if (!price || !compare) return;

    price.textContent = variant?.priceFormatted || '';

    const variantPrice = Number(variant?.price || 0);
    const variantCompareAt = Number(variant?.compareAt || 0);
    const hasCompareAt = variantCompareAt > variantPrice && variantPrice > 0;

    compare.hidden = !hasCompareAt;

    if (hasCompareAt) {
      compare.textContent = variant.compareAtFormatted || '';
    } else {
      compare.textContent = '';
    }
  };

  const updateKitCards = (bundle, selectedKit) => {
    bundle.querySelectorAll('[data-magnefit-card]').forEach((card) => {
      const isSelected = card.dataset.magnefitKit === selectedKit;
      const trigger = card.querySelector('[data-magnefit-offer]');
      const panel = card.querySelector('[data-magnefit-panel]');

      card.classList.toggle('is-selected', isSelected);

      if (trigger) {
        trigger.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
        trigger.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      }

      if (panel) {
        panel.hidden = !isSelected;
      }
    });
  };

  const updateSizeButtons = (bundle, selectedSize) => {
    bundle.querySelectorAll('[data-magnefit-size]').forEach((button) => {
      const isSelected = button.dataset.magnefitSize === selectedSize;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  };

  const setStatus = (bundle, message = '') => {
    const status = bundle.querySelector('[data-magnefit-status]');
    if (!status) return;

    status.textContent = message;
    status.hidden = message === '';
  };

  const setSubmitState = (bundle, variant) => {
    const submit = bundle.querySelector('[data-magnefit-submit]');
    const submitText = bundle.querySelector('[data-magnefit-submit-text]');
    const variantInput = bundle.querySelector('[data-magnefit-variant-input]');
    const quantityInput = bundle.querySelector('[data-magnefit-quantity-input]');
    const isAvailable = Boolean(variant?.available);

    if (variantInput && variant?.id) {
      variantInput.value = String(variant.id);
      variantInput.disabled = !isAvailable;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (quantityInput) {
      quantityInput.value = '1';
    }

    if (submit) {
      submit.disabled = !isAvailable;
      submit.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    }

    if (submitText) {
      submitText.textContent = isAvailable ? 'Agregar al carrito' : 'Agotado';
    }

    setStatus(bundle, isAvailable ? '' : 'Esta combinacion esta agotada por el momento.');
  };

  const updateBundle = (bundle, variants) => {
    const state = getState(bundle);
    const variant = getVariant(variants, state.kit, state.size);
    const expectedProductId = bundle.dataset.expectedProductId;
    const currentProductId = bundle.dataset.productId;
    const productMatches = expectedProductId === currentProductId;

    updateKitCards(bundle, state.kit);
    updateSizeButtons(bundle, state.size);

    ALLOWED_KITS.forEach((kit) => updateCardPrice(bundle, variants, kit, state.size));

    if (!productMatches) {
      setSubmitState(bundle, null);
      setStatus(bundle, 'Este bundle no corresponde al producto actual.');
      return;
    }

    setSubmitState(bundle, variant);
  };

  const initBundle = (bundle) => {
    if (initializedBundles.has(bundle)) return;
    initializedBundles.add(bundle);

    const variants = parseVariantData(bundle);
    setState(bundle, {
      kit: bundle.dataset.defaultKit || '2',
      size: bundle.dataset.defaultSize || 'S'
    });

    bundle.querySelectorAll('[data-magnefit-offer]').forEach((button) => {
      button.addEventListener('click', () => {
        const card = button.closest('[data-magnefit-card]');
        setState(bundle, {
          kit: card?.dataset.magnefitKit,
          size: getState(bundle).size
        });
        updateBundle(bundle, variants);
      });
    });

    bundle.querySelectorAll('[data-magnefit-size]').forEach((button) => {
      button.addEventListener('click', () => {
        setState(bundle, {
          kit: getState(bundle).kit,
          size: button.dataset.magnefitSize
        });
        updateBundle(bundle, variants);
      });
    });

    const form = bundle.querySelector('form[data-type="add-to-cart-form"]');
    form?.addEventListener('submit', () => {
      const state = getState(bundle);
      const variant = getVariant(variants, state.kit, state.size);
      setSubmitState(bundle, variant);

      const submit = bundle.querySelector('[data-magnefit-submit]');
      const submitText = bundle.querySelector('[data-magnefit-submit-text]');

      if (submit && variant?.available) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }

      if (submitText && variant?.available) {
        submitText.textContent = 'Agregando...';
      }
    });

    updateBundle(bundle, variants);
  };

  const initAll = () => {
    document.querySelectorAll('[data-magnefit-bundle]').forEach(initBundle);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  window.ThorbesMagneFitBundle = {
    getVariantKey,
    getVariant
  };
})();
